import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../config/apiConfig';
import { fetchWeakQuestions as fetchWeakQuestionsData } from '../utils/testTracker';
import './Dashboard.css';


const Dashboard = () => {
  const { token, logout } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [recentTests, setRecentTests] = useState([]);
  const [weakQuestions, setWeakQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const navigate = useNavigate();
  const googlePlayAppUrl = 'https://play.google.com/store/apps/details?id=com.codewithnguyen.todoalbertadriving';
  const qrCodeUrl = `${process.env.PUBLIC_URL || ''}/assets/qrcode.png`;
  const googlePlayBadgeUrl = `${process.env.PUBLIC_URL || ''}/assets/ongoogleplay.png`;

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.PROFILE, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Safe JSON parsing
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('JSON parse error fetching profile:', parseError);
        throw new Error('Server error: Invalid response from profile endpoint');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch profile');
      }

      setUserProfile(data.user);
    } catch (err) {
      console.error('Profile fetch error:', err);
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchRecentTests = useCallback(async () => {
    try {
      const response = await fetch(API_ENDPOINTS.RECENT_TESTS, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('JSON parse error fetching recent tests:', parseError);
        return;
      }

      if (response.ok && data.recentTests) {
        setRecentTests(data.recentTests);
      }
    } catch (err) {
      console.error('Recent tests fetch error:', err);
    }
  }, [token]);

  const fetchWeakQuestions = useCallback(async () => {
    try {
      const data = await fetchWeakQuestionsData(token, {
        testType: 'practice',
        limit: 6,
        minFailures: 1
      });
      setWeakQuestions(data.weakQuestions || []);
    } catch (err) {
      console.error('Weak questions fetch error:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchUserProfile();
    fetchRecentTests();
    fetchWeakQuestions();
  }, [fetchUserProfile, fetchRecentTests, fetchWeakQuestions]);


  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.PASSWORD_CHANGE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      // Safe JSON parsing
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('JSON parse error changing password:', parseError);
        throw new Error('Server error: Invalid response');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update password');
      }

      setPasswordSuccess('Password updated successfully!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => {
        setShowPasswordChange(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (err) {
      setPasswordError(err.message);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleResumeTest = async (test) => {
    try {
      // Log test access
      await fetch(API_ENDPOINTS.TEST_ACCESS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          testType: test.test_type,
          testName: test.test_name,
          testCategory: test.test_category
        })
      });

      // Navigate to appropriate test page
      if (test.test_type === 'practice') {
        navigate('/practice', { state: { resumeTest: test } });
      } else if (test.test_type === 'mock') {
        navigate('/mock-test', { state: { resumeTest: test } });
      }
    } catch (err) {
      console.error('Error resuming test:', err);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <button onClick={handleLogout} className="logout-button">Logout</button>
      </div>

      <div className="dashboard-content">
        <div className="profile-card">
          <h2>User Profile</h2>
          <div className="profile-info">
            <div className="info-row">
              <span className="info-label">Username:</span>
              <span className="info-value">{userProfile?.username}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Email:</span>
              <span className="info-value">{userProfile?.email}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Member Since:</span>
              <span className="info-value">
                {new Date(userProfile?.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        <div className="subscription-card">
          <h2>Subscription</h2>
          <div className="subscription-info">
            <div className="subscription-badge">
              <span className={`badge ${userProfile?.subscription}`}>
                {userProfile?.subscription?.toUpperCase()}
              </span>
            </div>
            <p className="subscription-description">
              {userProfile?.subscription === 'free' 
                ? 'Upgrade to Premium for unlimited access to all mock tests and practice materials!'
                : 'You have full access to all premium features!'}
            </p>
            {userProfile?.subscription === 'free' && (
              <button 
                onClick={() => navigate('/checkout')}
                className="subscription-button upgrade"
              >
                Upgrade to Premium
              </button>
            )}
            {userProfile?.subscription === 'premium' && (
              <div className="premium-notice">
                <p>✅ Premium upgrades cannot be downgraded. Enjoy your lifetime access!</p>
              </div>
            )}
          </div>
        </div>

        <div className="password-card">
          <h2>Security</h2>
          {!showPasswordChange ? (
            <button 
              onClick={() => setShowPasswordChange(true)}
              className="change-password-button"
            >
              🔐 Change Password
            </button>
          ) : (
            <form onSubmit={handlePasswordChange} className="password-form">
              {passwordError && <div className="error-message">{passwordError}</div>}
              {passwordSuccess && <div className="success-message">{passwordSuccess}</div>}
              
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  placeholder="Enter current password"
                />
              </div>

              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                  placeholder="Enter new password"
                />
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                  placeholder="Confirm new password"
                />
              </div>

              <div className="password-buttons">
                <button type="submit" className="save-button">Save Password</button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowPasswordChange(false);
                    setPasswordData({currentPassword: '', newPassword: '', confirmPassword: ''});
                    setPasswordError('');
                  }}
                  className="cancel-button"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {recentTests.length > 0 && (
          <div className="recent-tests-card">
            <h2>Continue Your Learning 🚀</h2>
            <div className="recent-tests-list">
              {recentTests.map((test) => (
                <div key={test.id} className="recent-test-item">
                  <div className="test-info">
                    <div className="test-header">
                      <span className="test-type-badge" data-type={test.test_type}>
                        {test.test_type === 'practice' ? '📝 Practice' : '🎯 Mock Test'}
                      </span>
                      <span className="test-name">{test.test_name}</span>
                    </div>
                    {test.category && (
                      <span className="test-category">{test.test_category}</span>
                    )}
                    {test.progress_percentage > 0 && (
                      <div className="progress-info">
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${test.progress_percentage}%` }}
                          ></div>
                        </div>
                        <span className="progress-text">{test.progress_percentage}% Complete</span>
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={() => handleResumeTest(test)}
                    className="resume-button"
                  >
                    Resume →
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {weakQuestions.length > 0 && (
          <div className="weak-questions-card">
            <h2>Questions To Recheck Before Exam</h2>
            <p className="weak-questions-note">These are your most frequently missed practice questions.</p>
            <div className="weak-questions-dashboard-list">
              {weakQuestions.map((question) => (
                <button
                  key={question.question_id}
                  className="weak-question-row"
                  onClick={() => navigate('/practice', { state: { focusQuestionId: question.question_id } })}
                >
                  <span className="weak-question-title">Question #{question.question_id}</span>
                  <span className="weak-question-stats">{question.failures} fails • {question.failure_rate || 0}% fail rate</span>
                  <span className="weak-question-action">Recheck →</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="quick-links-card">
          <h2>Quick Links</h2>
          <div className="quick-links">
            <button onClick={() => navigate('/learning')} className="link-button learning-btn">
              <span className="btn-icon">📚</span>
              <span className="btn-text">Learning Sections</span>
              <span className="btn-arrow">→</span>
            </button>
            <button onClick={() => navigate('/practice')} className="link-button practice-btn">
              <span className="btn-icon">📝</span>
              <span className="btn-text">Practice Tests</span>
              <span className="btn-arrow">→</span>
            </button>
            <button onClick={() => navigate('/mock-test')} className="link-button mock-btn">
              <span className="btn-icon">🎯</span>
              <span className="btn-text">Mock Tests</span>
              <span className="btn-arrow">→</span>
            </button>
            <button onClick={() => navigate('/')} className="link-button home-btn">
              <span className="btn-icon">🏠</span>
              <span className="btn-text">Home</span>
              <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>

        <div className="playstore-app-card">
          <h2>Download Our App</h2>
          <p className="playstore-app-subtitle">Learn on Google Play</p>
          <p className="playstore-app-description">Scan the QR code or tap the button below to download the Alberta Class 7 prep app.</p>
          <a
            href={googlePlayAppUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="playstore-app-qr-link"
            aria-label="Open app on Google Play"
          >
            <img
              src={qrCodeUrl}
              alt="QR code to download app on Google Play"
              className="playstore-app-qr"
            />
          </a>
          <a
            href={googlePlayAppUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="playstore-app-badge-link"
            aria-label="Get it on Google Play"
          >
            <img
              src={googlePlayBadgeUrl}
              alt="Get it on Google Play"
              className="playstore-app-badge"
            />
          </a>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
